import { useState } from "react";
import { useSearchParams } from "react-router";

import { useUrlBuilder } from "apps/commons/hooks/useUrlBuilder";
import { automaticImportApi } from "libs/api";
import { useNavigate, useParams } from "router";
import type {
    ExtendedScrapingErrorResponse,
    ExtendedScrapingErrorResponseErrorTypeEnum,
    PropertySelectionItem,
} from "../../../libs/codegen/property-onboarding";
import type { FooterProps } from "../Footer";
import { clearCampaignParams, getCampaignParams } from "../campaignParams";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { scrapingErrorResponseMapper } from "../urlInput/scrapingErrorResponseMapper.ts";
import { usePropertiesValue } from "./propertiesAtom";
import { selectablePropertiesFilter } from "./selectablePropertiesFilter";
import { useSelectedPropertiesValue } from "./selectedPropertiesAtom";

export const useAutomaticListsHandler = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ExtendedScrapingErrorResponseErrorTypeEnum | null>(null);

    const {
        automaticPropertySelectionCms: { back, importListings, importListingsWithNumber, goToListing },
    } = usePropertySelectionViewModelValue();
    const navigate = useNavigate();
    const { lang } = useParams("/:lang/list-my-property/get-started/automatic/lists");
    const urlBuilder = useUrlBuilder();
    const [searchParams] = useSearchParams();
    const campaignParams = getCampaignParams(searchParams);

    const selectedProperties = useSelectedPropertiesValue();
    const properties = usePropertiesValue();

    const selectableProperties = properties.filter(selectablePropertiesFilter);

    const onSecondary = () => {
        navigate("/:lang/list-my-property/get-started/automatic", {
            params: {
                lang,
            },
        });
    };

    const onPrimary = async () => {
        // if no selectable properties, go to listing page
        if (selectableProperties.length === 0) {
            window.location.assign(urlBuilder.listingsUrl());
        } else {
            setLoading(true);
            setError(null);

            if (selectedProperties.length === 0) {
                setError(scrapingErrorResponseMapper.EmptyInput);
                setLoading(false);
            } else {
                try {
                    const { data } = await automaticImportApi.importProperty(lang, {
                        selected: (
                            selectableProperties.filter((o) =>
                                selectedProperties.includes(o.id),
                            ) as PropertySelectionItem[]
                        ).map((o) => ({
                            ...o,
                            campaignId: campaignParams.ocid,
                            encryptedEmail: campaignParams.e,
                        })),
                        unselected: (
                            selectableProperties.filter(
                                (o) => !selectedProperties.includes(o.id),
                            ) as PropertySelectionItem[]
                        ).map((o) => ({
                            ...o,
                            campaignId: campaignParams.ocid,
                            encryptedEmail: campaignParams.e,
                        })),
                    });

                    clearCampaignParams();
                    window.location.assign(data.redirectTo);
                } catch (e) {
                    // If scraping err
                    if (typeof e?.data?.errorType === "number") {
                        const errorResponse = e.data as ExtendedScrapingErrorResponse;
                        setError(errorResponse.errorType);
                    } else {
                        setError(scrapingErrorResponseMapper.UnexpectedError);
                    }

                    setLoading(false);
                }
            }
        }
    };

    const footerProps: FooterProps = {
        secondary: {
            label: back,
            dataElementName: "back",
        },
        primary: {
            // eslint-disable-next-line no-nested-ternary
            label:
                selectableProperties.length === 0
                    ? goToListing
                    : selectedProperties.length === 0
                      ? importListings
                      : importListingsWithNumber.replace("{0}", selectedProperties.length.toString()),
            loading,
            dataElementName: "import-button",
        },
        onSecondary,
        onPrimary,
    };

    return {
        ...footerProps,
        error,
    };
};
